/**
 * AestheticPicker — "What does your work feel like?" step.
 *
 * Eight vibe options. The user picks up to MAX_SELECTIONS. The choices
 * become hints for Nova's future suggestions and the studio's default
 * theme. Stored on `organization.aesthetic_tags` (text[]).
 */

import { Check } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { cn } from '@/lib/utils';

export type AestheticTag =
  | 'cinematic'
  | 'editorial'
  | 'documentary'
  | 'fine_art'
  | 'vibrant'
  | 'moody'
  | 'minimalist'
  | 'romantic';

interface AestheticOption {
  id: AestheticTag;
  label: string;
  description: string;
}

export const aestheticOptions: AestheticOption[] = [
  { id: 'cinematic', label: 'Cinematic', description: 'warm tones, slow movement' },
  { id: 'editorial', label: 'Editorial', description: 'high-fashion, magazine-ready' },
  { id: 'documentary', label: 'Documentary', description: 'real moments, real people' },
  { id: 'fine_art', label: 'Fine art', description: 'painterly, intentional, slow' },
  { id: 'vibrant', label: 'Vibrant', description: 'saturated, joyful, alive' },
  { id: 'moody', label: 'Moody', description: 'shadows, grain, atmosphere' },
  { id: 'minimalist', label: 'Minimalist', description: 'negative space, geometry' },
  { id: 'romantic', label: 'Romantic', description: 'soft light, intimate moments' },
];

export const MAX_AESTHETIC_SELECTIONS = 3;

interface AestheticPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export const AestheticPicker = ({ value, onChange }: AestheticPickerProps) => {
  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else if (value.length < MAX_AESTHETIC_SELECTIONS) {
      onChange([...value, id]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {aestheticOptions.map((opt) => {
          const isSelected = value.includes(opt.id);
          const isDisabled = !isSelected && value.length >= MAX_AESTHETIC_SELECTIONS;

          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              disabled={isDisabled}
              className={cn(
                'relative rounded-xl border-2 p-3 text-left transition-all duration-150',
                isSelected
                  ? 'border-primary bg-primary/10 shadow-sm shadow-primary/20'
                  : isDisabled
                    ? 'border-border/50 bg-muted/20 opacity-50 cursor-not-allowed'
                    : 'border-border bg-secondary/30 hover:border-primary/40 hover:bg-secondary/50',
              )}
            >
              {isSelected && (
                <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  <OrbitIcon name="checkmark" fallback={Check} className="w-2.5 h-2.5" strokeWidth={3} />
                </div>
              )}
              <h4
                className={cn(
                  'font-semibold text-sm leading-tight',
                  isSelected ? 'text-primary' : 'text-foreground',
                )}
              >
                {opt.label}
              </h4>
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                {opt.description}
              </p>
            </button>
          );
        })}
      </div>

      <p className="text-center text-xs text-lunar-teal" data-testid="aesthetic-counter">
        {value.length} of {MAX_AESTHETIC_SELECTIONS} selected
      </p>
    </div>
  );
};
